// SHADOW CHAT — Realtime-ish floating chat panel (additive).
//
// Polls /api/shadow-chat every 3 seconds for new messages. All authenticated
// users can read, PRO/admin can write. Admins can delete any message.
// Sticky at the bottom of its container on desktop, fixed bottom on mobile.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  MessageSquare, Send, Trash2, ChevronDown, ShieldCheck,
  Loader2, Crown, Sparkles,
} from "lucide-react";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";

const AVATAR_PALETTE = ["#00d4ff", "#fbbf24", "#10b981", "#a78bfa", "#fb923c", "#3b82f6", "#ec4899", "#ef4444"];
function avatarColor(id = "") {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function fmtRelative(iso, nowLabel) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return nowLabel;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return new Date(iso).toLocaleDateString();
}

export const ShadowChat = () => {
  const { user, openLogin } = useAuth();
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const lastTsRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const scrollerRef = useRef(null);
  const endRef = useRef(null);

  const canWrite = !!user && (user.is_pro || user.is_admin);
  const isAdmin = !!user?.is_admin;

  const scrollToBottom = useCallback((smooth = true) => {
    endRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/shadow-chat`);
        const data = await safeJson(res);
        if (res.ok && data?.messages) {
          setMessages(data.messages);
          if (data.messages.length) lastTsRef.current = data.messages.at(-1).created_at;
        }
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // Poll every 3s
  useEffect(() => {
    const poll = async () => {
      try {
        const since = lastTsRef.current;
        const url = since
          ? `${API}/shadow-chat?since=${encodeURIComponent(since)}`
          : `${API}/shadow-chat`;
        const res = await fetch(url);
        const data = await safeJson(res);
        if (!res.ok || !data?.messages?.length) return;

        // Filter out any that we already have (re-entrant safety).
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const fresh = data.messages.filter((m) => !ids.has(m.id));
          if (!fresh.length) return prev;
          lastTsRef.current = fresh.at(-1).created_at;
          if (!open) setUnread((n) => n + fresh.length);
          else if (isNearBottomRef.current) setTimeout(scrollToBottom, 50);
          return [...prev, ...fresh];
        });
      } catch {
        /* network blip — ignore */
      }
    };
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [open, scrollToBottom]);

  // When opening, reset unread + bring user to bottom
  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => scrollToBottom(false), 60);
    }
  }, [open, scrollToBottom]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError("");
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ text }),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.message || data?.detail || data?.error || "Error al enviar";
        throw new Error(typeof msg === "string" ? msg : "Error al enviar");
      }
      setMessages((prev) => [...prev, data.message]);
      lastTsRef.current = data.message.created_at;
      setInput("");
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      setError(e.message || "Error");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const remove = async (id) => {
    if (!window.confirm(ui.chat_confirm_delete)) return;
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-chat/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (res.ok) setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch {}
  };

  const charLeft = 500 - input.length;

  return (
    <div className="shadow-chat-wrap" data-testid="shadow-chat">
      {/* HEADER (clickable to toggle) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 border border-white/[0.07] bg-zinc-950/85 px-3.5 py-2.5 text-left backdrop-blur transition-colors hover:border-cyan-400/40 ${open ? "rounded-t-xl" : "rounded-xl"}`}
        data-testid="shadow-chat-toggle"
      >
        <MessageSquare className="h-3.5 w-3.5 text-cyan-300" />
        <span className="flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-white">
          {ui.chat_title}
        </span>
        <span className="rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-1.5 py-0.5 font-mono text-[8px] font-black tracking-wider text-cyan-200">
          PRO
        </span>
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/80" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
        </span>
        {unread > 0 && (
          <span
            className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 font-mono text-[9px] font-black text-white"
            data-testid="shadow-chat-unread"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="text-white/40"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </motion.span>
      </button>

      {/* BODY */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.33, 1, 0.68, 1] }}
            className="overflow-hidden border-x border-b border-white/[0.07] bg-zinc-950/85 backdrop-blur rounded-b-xl"
          >
            <p className="border-b border-white/[0.04] bg-black/30 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white/40">
              {ui.chat_pro_writes_all_read}
            </p>

            {/* Messages */}
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              className="flex h-[280px] flex-col gap-2.5 overflow-y-auto bg-black/20 px-3 py-3 shadow-feed-desc"
              data-testid="shadow-chat-messages"
            >
              {!loaded ? (
                <div className="m-auto flex items-center gap-2 text-[11px] text-white/40">
                  <Loader2 className="h-3 w-3 animate-spin" /> {ui.chat_loading}
                </div>
              ) : messages.length === 0 ? (
                <div className="m-auto text-center text-[11px] text-white/35">
                  {ui.chat_first_msg}
                </div>
              ) : (
                messages.map((m) => {
                  const u = m.user || {};
                  const isMe = user && u.id === user.id;
                  const isAdminMsg = !!u.is_admin;
                  return (
                    <div
                      key={m.id}
                      className={`flex items-start gap-2 ${
                        isAdminMsg
                          ? "rounded-r border-l-2 border-cyan-400 bg-cyan-400/[0.06] py-1 pl-2 pr-1"
                          : isMe
                          ? "rounded bg-cyan-400/[0.04] px-1.5 py-1"
                          : ""
                      }`}
                      data-testid={`shadow-chat-msg-${m.id}`}
                    >
                      <div
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-black text-[#07080f]"
                        style={{ background: avatarColor(u.id || m.id) }}
                        aria-hidden
                      >
                        {(u.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`truncate text-[11px] font-bold ${isAdminMsg ? "text-cyan-200" : "text-white/85"}`}
                          >
                            {u.name}
                          </span>
                          {isAdminMsg && (
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-cyan-400/40 bg-cyan-400/[0.08] px-1 py-px font-mono text-[8px] font-black uppercase tracking-[0.15em] text-cyan-200">
                              <ShieldCheck className="h-2 w-2" /> {ui.chat_admin_label}
                            </span>
                          )}
                          {!isAdminMsg && u.is_ambassador && (
                            <Crown className="h-2.5 w-2.5 text-amber-300" />
                          )}
                          {!isAdminMsg && u.is_pro && (
                            <span className="rounded border border-cyan-400/25 px-1 font-mono text-[7px] font-bold tracking-widest text-cyan-300">
                              PRO
                            </span>
                          )}
                          <span className="ml-auto font-mono text-[9px] text-white/30">
                            {fmtRelative(m.created_at, ui.chat_relative_now)}
                          </span>
                          {isAdmin && (
                            <button
                              onClick={() => remove(m.id)}
                              className="text-white/30 transition-colors hover:text-rose-300"
                              aria-label={ui.chat_delete}
                              data-testid={`shadow-chat-delete-${m.id}`}
                            >
                              <Trash2 className="h-2.5 w-2.5" />
                            </button>
                          )}
                        </div>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-[12px] leading-relaxed text-white/80">
                          {m.text}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={endRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-white/[0.06] bg-zinc-950/60 px-3 py-2.5">
              {canWrite ? (
                <>
                  <div className="flex items-end gap-1.5">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value.slice(0, 500))}
                      onKeyDown={onKey}
                      placeholder={ui.chat_input_ph}
                      rows={1}
                      maxLength={500}
                      className="max-h-20 flex-1 resize-none rounded-lg border border-white/10 bg-black/40 px-2.5 py-1.5 text-[12px] leading-snug outline-none focus:border-cyan-400/60"
                      data-testid="shadow-chat-input"
                    />
                    <button
                      onClick={send}
                      disabled={!input.trim() || sending}
                      className="inline-flex h-[34px] items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-400/[0.1] px-2.5 text-cyan-200 transition-colors hover:bg-cyan-400/[0.2] disabled:opacity-40"
                      data-testid="shadow-chat-send"
                      aria-label={ui.chat_send}
                    >
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    {error ? (
                      <span className="text-[10px] text-rose-300" data-testid="shadow-chat-error">{error}</span>
                    ) : (
                      <span className="text-[9px] text-white/30">{ui.chat_enter_send_shift_newline}</span>
                    )}
                    <span
                      className="font-mono text-[9px]"
                      style={{ color: charLeft < 10 ? "#f87171" : charLeft < 50 ? "#fbbf24" : "rgba(255,255,255,0.3)" }}
                    >
                      {charLeft}
                    </span>
                  </div>
                </>
              ) : user ? (
                <div className="rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-2 text-center">
                  <p className="text-[10px] text-white/55">{ui.chat_pro_only_writes}</p>
                  <a
                    href="/#pricing"
                    className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-cyan-400/50 px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-[0.18em] text-cyan-200 hover:bg-cyan-400/10"
                    data-testid="shadow-chat-cta-pro"
                  >
                    <Sparkles className="h-2.5 w-2.5" /> {ui.chat_pro_cta}
                  </a>
                </div>
              ) : (
                <div className="rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-2 text-center">
                  <p className="text-[10px] text-white/55">{ui.chat_login_required}</p>
                  <button
                    onClick={openLogin}
                    className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-white/15 px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-white/70 hover:bg-white/[0.05]"
                    data-testid="shadow-chat-cta-login"
                  >
                    {ui.chat_login_cta}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShadowChat;
